import React, { useState, useMemo } from 'react';
import { ClientEntity, Showcase, ShowcaseItem } from '../../../types';
import { ChevronLeft, Save, Copy, ExternalLink, Presentation as PresentationIcon, Link as LinkIcon, Trash2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

interface ShowcaseEditorProps {
  showcase: Showcase;
  clients: ClientEntity[];
  onSave: (updated: Showcase) => void;
  onClose: () => void;
}

export const ShowcaseEditor: React.FC<ShowcaseEditorProps> = ({ showcase, clients, onSave, onClose }) => {
  const [name, setName] = useState(showcase.name);
  const [items, setItems] = useState<ShowcaseItem[]>(showcase.items);
  const [activeTab, setActiveTab] = useState<'sites_nyroh' | 'selecionar_nichos' | 'links_extras'>('sites_nyroh');

  // External Links State
  const [extTitle, setExtTitle] = useState('');
  const [extUrl, setExtUrl] = useState('');

  // Derived state for Niche processing
  const internalItems = useMemo(() => items.filter(i => i.type === 'internal'), [items]);
  const externalItems = useMemo(() => items.filter(i => i.type === 'external'), [items]);
  
  const nicheCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    internalItems.forEach(item => {
      counts[item.tabCategory] = (counts[item.tabCategory] || 0) + 1;
    });
    return counts;
  }, [internalItems]);

  const displayedNiches = useMemo(() => {
    const nicheMap: Record<string, number> = {};
    clients.forEach(c => {
      (c.data.categories || []).forEach((cat: string) => {
        nicheMap[cat] = (nicheMap[cat] || 0) + 1;
      });
    });
    return Object.entries(nicheMap)
      .map(([niche, available]) => ({ name: niche, available }))
      .sort((a, b) => b.available - a.available);
  }, [clients]);

  // Handlers
  const handleRemoveItem = (id: string) => {
    setItems(items.filter(i => i.id !== id));
  };

  const handleIncrementNiche = (niche: string) => {
    const currentCategoryClientIds = internalItems.filter(i => i.tabCategory === niche).map(i => i.clientId);
    const availableClients = clients.filter(c => (c.data.categories || []).includes(niche) && !currentCategoryClientIds.includes(c.id));
    
    if (availableClients.length > 0) {
      const randomClient = availableClients[Math.floor(Math.random() * availableClients.length)];
      const newItem: ShowcaseItem = {
        id: Date.now().toString(36) + Math.random().toString(36).substring(2, 9),
        type: 'internal',
        clientId: randomClient.id,
        tabCategory: niche
      };
      setItems(prev => [...prev, newItem]);
    }
  };

  const handleDecrementNiche = (niche: string) => {
    // Find last item of this niche and remove it
    const nicheItems = internalItems.filter(i => i.tabCategory === niche);
    if (nicheItems.length > 0) {
      const lastItem = nicheItems[nicheItems.length - 1];
      handleRemoveItem(lastItem.id);
    }
  };

  const handleAddExternal = () => {
    if (!extTitle.trim() || !extUrl.trim()) {
      toast.error('Preencha o título e a URL do link.');
      return;
    }
    
    let finalUrl = extUrl.trim();
    if (!/^https?:\/\//i.test(finalUrl)) {
      finalUrl = 'https://' + finalUrl;
    }

    const newItem: ShowcaseItem = {
      id: Date.now().toString(36) + Math.random().toString(36).substring(2, 9),
      type: 'external',
      externalTitle: extTitle,
      externalUrl: finalUrl,
      tabCategory: 'Outros'
    };

    setItems(prev => [...prev, newItem]);
    setExtTitle('');
    setExtUrl('');
    toast.success('Link externo adicionado com sucesso!');
  };

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Dê um nome para a vitrine.');
      return;
    }
    onSave({ ...showcase, name, items });
    toast.success('Alterações salvas com sucesso!');
  };

  const copyLink = () => {
    const url = `${window.location.origin}/#/showcase/${showcase.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link público copiado!');
  };

  const testPreview = () => {
    const url = `${window.location.origin}/#/showcase/${showcase.slug}`;
    window.open(url, '_blank');
  };

  // Group internal items by category for rendering
  const groupedInternal = useMemo(() => {
    const groups: Record<string, ShowcaseItem[]> = {};
    internalItems.forEach(item => {
      if (!groups[item.tabCategory]) groups[item.tabCategory] = [];
      groups[item.tabCategory].push(item);
    });
    return groups;
  }, [internalItems]);

  return (
    <div className="flex flex-col h-full bg-[#FAFAFA] font-sans">
      
      {/* HEADER: Mesa de Operações */}
      <header className="h-20 bg-white border-b border-gray-200 px-6 lg:px-10 flex items-center justify-between shrink-0 shadow-sm z-20">
         <div className="flex items-center gap-4 flex-1">
            <button onClick={onClose} className="p-2 -ml-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-xl transition-all" title="Voltar ao Painel">
               <ChevronLeft size={24} />
            </button>
            <div className="h-8 w-px bg-gray-200"></div>
            <div className="flex flex-col">
               <span className="text-[10px] font-bold tracking-widest text-indigo-500 uppercase">Portfólio Ativo</span>
               <input 
                 value={name} 
                 onChange={e => setName(e.target.value)} 
                 className="text-2xl font-black text-gray-900 bg-transparent border-none p-0 focus:ring-0 placeholder-gray-300 w-full hover:bg-gray-50 transition-colors rounded outline-none" 
                 placeholder="Nome da Vitrine" 
                 title="Clique para renomear"
               />
            </div>
         </div>

         <div className="flex items-center gap-3 shrink-0">
            <button onClick={testPreview} className="hidden sm:flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition-all">
               <ExternalLink size={16} /> Ver como Cliente
            </button>
            <button onClick={copyLink} className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition-all">
               <Copy size={16} /> Copiar Link
            </button>
            <button onClick={handleSave} className="flex items-center gap-2 px-6 py-2.5 bg-black hover:bg-indigo-600 text-white shadow-md border border-transparent rounded-lg text-sm font-bold transition-all">
               <Save size={16} /> Salvar Alterações
            </button>
         </div>
      </header>

      {/* ABAS DEFINITIVAS */}
      <div className="bg-white px-6 lg:px-10 border-b border-gray-100 flex items-center gap-2 shrink-0 py-3">
         <button 
           onClick={() => setActiveTab('sites_nyroh')}
           className={`px-5 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all ${activeTab === 'sites_nyroh' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50 hover:text-black border border-gray-100'}`}
         >
           Sites na Vitrine ({internalItems.length})
         </button>

         <button 
           onClick={() => setActiveTab('selecionar_nichos')}
           className={`px-5 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all flex items-center gap-2 ${activeTab === 'selecionar_nichos' ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-600' : 'text-indigo-500 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100'}`}
         >
           + Selecionar Nichos
         </button>

         <div className="h-6 w-px bg-gray-200 mx-2 hidden sm:block"></div>
         
         <button 
           onClick={() => setActiveTab('links_extras')}
           className={`px-5 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all flex items-center gap-2 ${activeTab === 'links_extras' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50 hover:text-black border border-gray-100'}`}
         >
           <LinkIcon size={14} /> Links Extras ({externalItems.length})
         </button>
      </div>

      {/* ÁREA CENTRAL DE EDIÇÃO */}
      <main className="flex-1 overflow-y-auto p-6 lg:p-10">
         <div className="max-w-7xl mx-auto">
            
            {/* TAB: SITES NYROH */}
            {activeTab === 'sites_nyroh' && (
               <div className="space-y-12 animate-in fade-in duration-300">
                  {Object.keys(groupedInternal).length === 0 ? (
                     <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                           <PresentationIcon size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Nenhum site adicionado</h3>
                        <p className="text-gray-500 text-sm max-w-sm">Vá na aba "Selecionar Nichos" para escolher as categorias e a quantidade de sites.</p>
                     </div>
                  ) : (
                     Object.entries(groupedInternal).map(([niche, nicheItems]) => (
                        <div key={niche}>
                           <div className="flex items-end justify-between mb-6 pb-2 border-b border-gray-200">
                              <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">{niche}</h2>
                              <span className="text-sm font-bold text-gray-400">{nicheItems.length} {nicheItems.length === 1 ? 'Site' : 'Sites'}</span>
                           </div>
                           <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                              {nicheItems.map(item => {
                                 const client = clients.find(c => c.id === item.clientId);
                                 return (
                                    <div key={item.id} className="relative bg-white border border-gray-200 rounded-2xl group overflow-hidden flex flex-col shadow-sm">
                                       <div className="h-48 relative bg-gray-50 border-b border-gray-100 overflow-hidden isolate">
                                          {item.type === 'internal' && client ? (
                                             <iframe 
                                               srcDoc={client.htmlContent} 
                                               className="absolute top-0 left-0 pointer-events-none bg-white border-0 z-0 select-none" 
                                               style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: 'top left' }} 
                                               title="preview" 
                                               tabIndex={-1} 
                                               sandbox="allow-scripts allow-same-origin allow-popups" 
                                             />
                                          ) : item.type === 'external' ? (
                                             <div className="w-full h-full relative bg-gray-50 flex flex-col items-center justify-center">
                                                <iframe 
                                                   src={item.externalUrl} 
                                                   className="absolute top-0 left-0 pointer-events-none bg-white border-0 z-0" 
                                                   style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: 'top left' }} 
                                                   title="external-preview"
                                                   sandbox="allow-scripts allow-same-origin allow-popups"
                                                />
                                             </div>
                                          ) : (
                                             <div className="flex items-center justify-center w-full h-full text-gray-300 bg-gray-50 z-0"><PresentationIcon size={40} /></div>
                                          )}
                                          
                                          {/* Hover action */}
                                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 flex items-center justify-center pointer-events-auto">
                                             <button 
                                                onClick={() => handleRemoveItem(item.id)} 
                                                className="bg-white hover:bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-all shadow-lg"
                                                title="Remover Site"
                                             >
                                                <Trash2 size={16} /> Remover
                                             </button>
                                          </div>
                                       </div>
                                       <div className="p-4 bg-white">
                                          <h3 className="font-bold text-gray-900 text-sm mb-1 truncate" title={item.type === 'internal' ? client?.data.name : item.externalTitle}>
                                             {item.type === 'internal' ? client?.data.name : item.externalTitle}
                                          </h3>
                                          <p className="text-xs text-gray-500 truncate">{item.type === 'internal' ? 'Template Nyroh Exclusivo' : item.externalUrl}</p>
                                       </div>
                                    </div>
                                 )
                              })}
                           </div>
                        </div>
                     ))
                  )}
               </div>
            )}

            {/* TAB: SELECIONAR NICHOS */}
            {activeTab === 'selecionar_nichos' && (
               <div className="animate-in fade-in duration-300">
                  <div className="mb-8">
                     <h2 className="text-2xl font-black text-gray-900 tracking-tight">Estoque de Curadoria</h2>
                     <p className="text-sm text-gray-500 mt-1">Determine e limite com exatidão a alocação dos nichos dentro desta vitrine.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                     {displayedNiches.map(({name: niche, available}) => {
                        const count = nicheCounts[niche] || 0;
                        return (
                          <div key={niche} className={`flex justify-between items-center p-3 rounded-xl border transition-colors ${count > 0 ? 'bg-black text-white border-black shadow-md' : 'bg-gray-50 border-gray-100 text-gray-700 hover:shadow-sm'}`}>
                             <div className="flex flex-col min-w-0 pr-2">
                               <span className="text-sm font-bold truncate leading-tight">{niche}</span>
                               <span className={`text-[10px] font-bold mt-1 tracking-wide ${count > 0 ? 'text-gray-400' : 'text-indigo-500'}`}>{available} NO BANCO</span>
                             </div>
                             <div className="flex items-center gap-2 shrink-0">
                               <button 
                                  onClick={() => handleDecrementNiche(niche)}
                                  disabled={count === 0}
                                  className={`w-7 h-7 text-base rounded-md flex items-center justify-center font-bold transition-all disabled:opacity-20 disabled:cursor-not-allowed ${count > 0 ? 'bg-white/20 hover:bg-white/40' : 'bg-white border border-gray-200 hover:bg-gray-100'}`}
                                >-</button>
                               <span className="w-5 text-center text-sm font-bold">{count}</span>
                               <button 
                                 disabled={count >= available} 
                                 onClick={() => handleIncrementNiche(niche)} 
                                 className={`w-7 h-7 text-base rounded-md flex items-center justify-center font-bold transition-all disabled:opacity-20 disabled:cursor-not-allowed ${count > 0 ? 'bg-white text-black hover:bg-gray-200' : 'bg-white border border-gray-200 hover:border-black hover:text-black'}`}
                               >+</button>
                             </div>
                          </div>
                        )
                     })}
                     {displayedNiches.length === 0 && <div className="col-span-full py-6 text-center text-sm text-gray-400">Nenhum nicho encontrado no seu banco de dados.</div>}
                  </div>
                  <div className="mt-6 flex justify-end">
                     <button onClick={() => setActiveTab('sites_nyroh')} className="bg-black hover:bg-indigo-600 text-white px-6 py-3 rounded-lg text-sm font-bold shadow-md flex items-center gap-2 transition-all">
                        Aplicar e Ver Vitrine <ArrowRight size={16} />
                     </button>
                  </div>
               </div>
            )}

            {activeTab === 'links_extras' && (
               <div className="animate-in fade-in duration-300">
                  <div className="mb-8">
                     <h2 className="text-2xl font-black text-gray-900 tracking-tight">Outros Referências</h2>
                     <p className="text-sm text-gray-500 mt-1">Insira referências de fora do sistema Nyroh para agregar à apresentação.</p>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm mb-8 flex flex-col md:flex-row gap-4 items-end">
                     <div className="flex-1 w-full">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Título da Referência</label>
                        <input 
                           value={extTitle} 
                           onChange={e => setExtTitle(e.target.value)} 
                           className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-black outline-none transition-all" 
                           placeholder="Ex: Proposta em PDF" 
                        />
                     </div>
                     <div className="flex-[2] w-full">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Link (URL)</label>
                        <input 
                           value={extUrl} 
                           onChange={e => setExtUrl(e.target.value)} 
                           className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-black outline-none transition-all" 
                           placeholder="https://..." 
                        />
                     </div>
                     <button 
                        onClick={handleAddExternal}
                        className="bg-black text-white px-8 py-3 rounded-lg text-sm font-bold shadow-md hover:bg-gray-800 transition-colors w-full md:w-auto h-[46px]"
                     >
                        Adicionar Link
                     </button>
                  </div>

                  {externalItems.length === 0 ? (
                     <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                           <LinkIcon size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Sem links externos</h3>
                        <p className="text-gray-500 text-sm max-w-sm">Adicione PDFs, propostas ou outros sites de fora da agência para complementar.</p>
                     </div>
                  ) : (
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {externalItems.map(item => (
                           <div key={item.id} className="relative bg-white border border-gray-200 rounded-2xl group overflow-hidden flex flex-col shadow-sm">
                              <div className="h-48 relative bg-gray-50 border-b border-gray-100 overflow-hidden isolate">
                                 <div className="w-full h-full relative bg-gray-50 flex flex-col items-center justify-center">
                                    <iframe 
                                       src={item.externalUrl} 
                                       className="absolute top-0 left-0 w-full h-full object-cover z-0 pointer-events-none bg-white border-0" 
                                       style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: 'top left' }} 
                                       title="preview"
                                       sandbox="allow-scripts allow-same-origin allow-popups"
                                    />
                                 </div>
                                 
                                 <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 flex items-center justify-center pointer-events-auto">
                                    <button 
                                       onClick={() => handleRemoveItem(item.id)} 
                                       className="bg-white hover:bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-all shadow-lg"
                                    >
                                       <Trash2 size={16} /> Remover
                                    </button>
                                 </div>
                              </div>
                              <div className="p-4 bg-white flex flex-col flex-1">
                                 <h3 className="font-bold text-gray-900 text-sm mb-1 truncate whitespace-normal line-clamp-1">{item.externalTitle}</h3>
                                 <div className="flex items-center gap-1.5 text-indigo-500 overflow-hidden mt-auto">
                                    <LinkIcon size={12} className="shrink-0" />
                                    <span className="text-[10px] font-bold truncate uppercase tracking-widest">{item.externalUrl}</span>
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>
                  )}
               </div>
            )}
         </div>
      </main>

    </div>
  );
};

